import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import { useAuth } from '../services/authContext';
import SetupGuard from '../components/SetupGuard';

const emptyForm = {
  firstName: '', lastName: '', email: '', password: '',
  grade: '', gender: '', dob: '', phone: '', address: '',
  guardianName: '', guardianContact: '', classId: '', active: true,
};

const Students = () => {
  const { user }                        = useAuth();
  const { toast }                       = useToast();
  const isAdmin                         = user?.role === 'admin';
  const [students, setStudents]         = useState([]);
  const [classes, setClasses]           = useState([]);
  const [programmes, setProgrammes]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [filterProg, setFilterProg]     = useState('');
  const [isModalOpen, setIsModalOpen]   = useState(false);
  const [current, setCurrent]           = useState(null);
  const [form, setForm]                 = useState(emptyForm);
  const [saving, setSaving]             = useState(false);
  const [selectedProg, setSelectedProg] = useState('');

  const loadData = async () => {
    try {
      setLoading(true); setError('');
      const [studentData, classData, progData] = await Promise.all([
        apiService.getAll('students'),
        apiService.getAll('classes'),
        apiService.getAll('programmes-list'),
      ]);
      setStudents(studentData  || []);
      setClasses(classData     || []);
      setProgrammes(progData   || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load learners.');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  // Group classes by programme for the class selector
  const classesByProg = useMemo(() =>
    classes.reduce((acc, c) => {
      const p = c.programme || 'Other';
      if (!acc[p]) acc[p] = [];
      acc[p].push(c);
      return acc;
    }, {}), [classes]);

  // Filter classes by selected programme in form
  const filteredClasses = useMemo(() =>
    selectedProg ? (classesByProg[selectedProg] || []) : classes,
    [selectedProg, classesByProg, classes]);

  const filtered = useMemo(() =>
    students.filter(s => {
      const matchSearch = [s.firstName, s.lastName, s.email, s.grade, s.className, s.guardianName].join(' ').toLowerCase().includes(search.toLowerCase());
      const matchProg   = filterProg ? s.className?.includes(filterProg) : true;
      return matchSearch && matchProg;
    }), [search, filterProg, students]);

  const openAdd = () => { setCurrent(null); setForm(emptyForm); setSelectedProg(''); setIsModalOpen(true); };

  const openEdit = student => {
    setCurrent(student);
    // Detect programme from class name
    const cls = classes.find(c => c._id === student.classIds?.[0]);
    setSelectedProg(cls?.programme || '');
    setForm({
      firstName: student.firstName || '', lastName: student.lastName || '',
      email: student.email || '', password: '',
      grade: student.grade || '', gender: student.gender || '',
      dob: student.dob || '', phone: student.phone || '',
      address: student.address || '', guardianName: student.guardianName || '',
      guardianContact: student.guardianContact || '',
      classId: student.classIds?.[0] || '', active: student.active ?? true,
    });
    setIsModalOpen(true);
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setSaving(true); setError('');
      const payload = { ...form, classId: form.classId || null };
      if (current) {
        await apiService.update('students', current._id, payload);
        toast('Learner updated successfully.', 'success');
      } else {
        await apiService.create('students', payload);
        toast('Learner enrolled successfully.', 'success');
      }
      setIsModalOpen(false); setForm(emptyForm);
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save learner.';
      setError(msg); toast(msg, 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this learner?')) return;
    try {
      await apiService.delete('students', id);
      toast('Learner deleted.', 'success');
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete.';
      setError(msg); toast(msg, 'error');
    }
  };

  const tableContent = (
    <div className="table-responsive">
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>{isAdmin ? 'Programme / Batch' : 'Assigned Class'}</th>
            <th>Year Level</th>
            <th>Guardian</th>
            {isAdmin && <th>Status</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={isAdmin ? 7 : 6} className="no-data-message">No learners found.</td></tr>
          ) : filtered.map(s => (
            <tr key={s._id}>
              <td><strong>{s.firstName} {s.lastName}</strong></td>
              <td>{s.email}</td>
              <td>{s.className || 'Not assigned'}</td>
              <td>{s.grade}</td>
              <td>
                <div>{s.guardianName || 'N/A'}</div>
                <small style={{ color: 'var(--text-light)' }}>{s.guardianContact || ''}</small>
              </td>
              {isAdmin && (
                <td>
                  <span style={{ background: s.active ? '#d4edda' : '#f8d7da', color: s.active ? '#155724' : '#721c24', padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.78rem' }}>
                    {s.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
              )}
              <td>
                <div className="action-buttons">
                  <button className="btn-icon" style={{ color: '#1976D2' }} onClick={() => window.location.href=`/attendance/student/${s._id}`}>Attendance</button>
                  <button className="btn-icon" style={{ color: '#7B1FA2' }} onClick={() => window.location.href=`/grades/student/${s._id}`}>Grades</button>
                  {isAdmin && <button className="btn-icon" onClick={() => openEdit(s)}>Edit</button>}
                  {isAdmin && <button className="btn-icon" style={{ color: '#c62828' }} onClick={() => handleDelete(s._id)}>Delete</button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <LoadingSpinner message="Loading learners..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{isAdmin ? 'Learners' : 'My Students'}</h1>
        {isAdmin && <button className="btn btn-primary" onClick={openAdd}>+ Enrol Learner</button>}
      </div>

      {error && <div className="error-message">{error}</div>}

      {isAdmin ? (
        <SetupGuard checks={[
          {
            ok: programmes.length > 0,
            icon: '🏛️',
            label: 'No Programmes exist yet',
            description: 'Create a programme before enrolling students so they can be assigned to a class.',
            action: { label: 'Create Programme', to: '/programmes' },
          },
          {
            ok: classes.length > 0,
            icon: '🏫',
            label: 'No Classes / Batches exist yet',
            description: 'Create at least one class or batch so students can be enrolled into it.',
            action: { label: 'Create Class', to: '/programmes' },
          },
        ]}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input style={{ flex: 1, minWidth: 200 }} type="text" placeholder="Search by name, class, year level or guardian..." value={search} onChange={e => setSearch(e.target.value)} />
            <select value={filterProg} onChange={e => setFilterProg(e.target.value)} style={{ minWidth: 200 }}>
              <option value="">All Programmes</option>
              {programmes.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          {tableContent}
        </SetupGuard>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <input style={{ flex: 1, minWidth: 200 }} type="text" placeholder="Search by name, class, year level or guardian..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {tableContent}
        </>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={current ? 'Edit Learner' : 'Enrol Learner'}>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input name="firstName" value={form.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input name="lastName" value={form.lastName} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input type="password" name="password" value={form.password} onChange={handleChange} required={!current} placeholder={current ? 'Leave blank to keep' : ''} />
            </div>
          </div>

          {/* Programme & Batch Enrollment */}
          <hr style={{ margin: '0.5rem 0', opacity: 0.3 }} />
          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-light)', marginBottom: '0.5rem', textTransform: 'uppercase' }}>Enrolment</div>
          <div className="form-row">
            <div className="form-group">
              <label>Programme</label>
              <select value={selectedProg} onChange={e => { setSelectedProg(e.target.value); setForm(p => ({ ...p, classId: '' })); }}>
                <option value="">Select Programme</option>
                {programmes.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Batch / Class</label>
              <select name="classId" value={form.classId} onChange={handleChange}>
                <option value="">Select Batch</option>
                {filteredClasses.map(c => (
                  <option key={c._id} value={c._id}>
                    {c.batchLabel ? `Y${c.yearLevel} ${c.semester} Batch ${c.batchLabel}` : c.name}
                    {c.studentCount !== undefined ? ` (${c.studentCount}/${c.maxStudents})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Year Level</label>
              <select name="grade" value={form.grade} onChange={handleChange} required>
                <option value="">Select Year Level</option>
                <option value="Year 1">Year 1</option>
                <option value="Year 2">Year 2</option>
                <option value="Year 3">Year 3</option>
              </select>
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select name="gender" value={form.gender} onChange={handleChange} required>
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <hr style={{ margin: '0.5rem 0', opacity: 0.3 }} />
          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <input type="date" name="dob" value={form.dob} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" value={form.phone} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Address</label>
            <input name="address" value={form.address} onChange={handleChange} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Guardian Name</label>
              <input name="guardianName" value={form.guardianName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Guardian Contact</label>
              <input name="guardianContact" value={form.guardianContact} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" name="active" id="student-active" checked={form.active} onChange={handleChange} />
            <label htmlFor="student-active" style={{ marginBottom: 0 }}>Active</label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : current ? 'Update Learner' : 'Enrol Learner'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Students;
